import { TextContent, Text, Level, LevelItem, FormGroup, TextInput } from '@patternfly/react-core';
import { ArrowRightIcon } from '@patternfly/react-icons'
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import InputLabel from '../../components/input-label';
import { FieldValidationState } from '../../components/validation-types';
import ValidationHelperText from '../../components/validation-helper-text';

import './customization-page.css';

interface CustomizationNameFieldProps {
    modelName: string,
    sourceCustomizationName: string,
    customizationNameValidation: FieldValidationState,
    onCustomizationNameChange: (newCustomizationName: string) => void,
    customizationName: string,
}

const CUSTOMIZATION_NAME_PATTERN = /^[a-z0-9_\-.]*$/;

const MAX_CUSTOMIZATION_NAME_LENGTH = 24;
const MAX_COMBINED_LENGTH = 31;

export const defaultDuplicatedModelNameValidation: FieldValidationState = { validated: 'default', helperTextInvalid: null };
export const emptyDuplicatedModelNameNameValidation: FieldValidationState = { validated: 'error', helperTextInvalid: 'New customization must be given a name.' };
export const duplicateNameDuplicatedModelNameNameValidation: FieldValidationState = { validated: 'error', helperTextInvalid: 'New customization must be given a unique name.' };
export const invalidPatternValidation: FieldValidationState = { validated: 'error', helperTextInvalid: 'Customization name can only contain lowercase letters, numbers, hyphens, underscores, and periods.' };
export const maxLengthValidation: FieldValidationState = { validated: 'error', helperTextInvalid: `Customization name must be fewer than ${MAX_CUSTOMIZATION_NAME_LENGTH} characters.` };

export const validateDuplicatedModelName = (
    allCustomizationNamesAndIds: { customizationName: string, modelId: string }[],
    requestedName: string,
    modelId?: string,
    modelName?: string
): FieldValidationState => {
    // Check for empty name first
    if (!requestedName) {
        return emptyDuplicatedModelNameNameValidation;
    }

    // Check customization name length
    if (requestedName.length >= MAX_CUSTOMIZATION_NAME_LENGTH) {
        return maxLengthValidation;
    }

    // Check combined length if modelName is provided
    if (modelName) {
        const combinedLength = modelName.length + requestedName.length + 1; // +1 for the dot
        if (combinedLength > MAX_COMBINED_LENGTH) {
            return { validated: 'error', helperTextInvalid: `Customization name must be fewer than ${MAX_COMBINED_LENGTH - modelName.length - 1} characters when combined with model name "${modelName}".` };
        }
    }

    // Validate against pattern
    if (!CUSTOMIZATION_NAME_PATTERN.test(requestedName)) {
        return invalidPatternValidation;
    }

    // Check for duplicate names
    if (allCustomizationNamesAndIds.some(cn =>
        cn.customizationName === requestedName &&
        (modelId === undefined || cn.modelId === modelId)
    )) {
        return duplicateNameDuplicatedModelNameNameValidation;
    }

    return defaultDuplicatedModelNameValidation;
}

const CustomizationNameField = (props: CustomizationNameFieldProps) => {
    const { modelName, sourceCustomizationName, customizationNameValidation, onCustomizationNameChange, customizationName: duplicatedCustomizationName } = props;
    const { t } = useTranslation();

    const handleCustomizationNameChange = useCallback((_: unknown, newCustomizationName: string) => {
        onCustomizationNameChange(newCustomizationName);
    }, [onCustomizationNameChange]);

    return (
        <>
            <TextContent className="model-name">
                <Text component="h6">{t('customizationPage.customizationNameField.title')}</Text>
            </TextContent>

            <Level className="ae-title-row duplicated-model-name-fields duplicated-name-fields-row" hasGutter>
                <LevelItem className="duplication-source-container">
                    <FormGroup
                        label={t('customizationPage.customizationNameField.duplicatingFrom')}
                        fieldId="duplication-source-name"
                        title={undefined}
                    >
                        <div className="duplication-source">
                            <span className="duplication-model-name text-nowrap">{modelName}</span>
                            <span className="duplication-separator">.</span>
                            <span className="duplication-customization-name text-nowrap">{sourceCustomizationName}</span>
                        </div>
                    </FormGroup>
                </LevelItem>
                <LevelItem className="duplication-arrow">
                    <ArrowRightIcon />
                </LevelItem>
                <LevelItem className="ae-title-aetitle-column">
                    <FormGroup
                        label={t('customizationPage.customizationNameField.newCustomization')}
                        fieldId="duplicated-model-name"
                        title={undefined}
                    >
                        <InputLabel label={`${modelName}.`} useRef className="duplicate-model-name-field">
                            <TextInput
                                type="text"
                                id="duplicated-model-name"
                                onChange={handleCustomizationNameChange}
                                value={duplicatedCustomizationName}
                                validated={customizationNameValidation.validated}
                                autoFocus
                                onFocus={(e) => setTimeout(() => e.target.focus(), 100)}
                            />
                        </InputLabel>
                        {customizationNameValidation.validated !== 'default' && (
                            <ValidationHelperText
                                validated={customizationNameValidation.validated}
                                helperText={customizationNameValidation.helperTextInvalid}
                            />
                        )}
                    </FormGroup>
                </LevelItem>
            </Level>
        </>
    );
}

export default CustomizationNameField;